import React, { useState, useEffect } from "react";
import {
    Heading,
    Container,
    Flex,
    Box,
    FormLabel,
    Input,
    FormControl,
    InputGroup,
    InputRightElement,
    Button,
    Spinner, Select, useToast
} from "@chakra-ui/react";
import MultiSelect from "react-multi-select-component";
import { useHistory } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";

import { Separator, ErrorBox, View, AddressSlider } from "@components";
import { IconLocation, IconTriangle, IconDate } from "@assets/Icons";
import { edit_profile } from "@lib/datahandlers/person";
import { editProfileCandidateSchema } from "@lib/validationData";
import { dataList } from "@lib/datahandlers/global"
import { removeState, saveState } from "@lib/localStorage";
import { loadState } from "@lib/localStorage";
import { t } from "@lang/i18n";

// Import React Script Libraray to load Google object
import Script from 'react-load-script';
import { getPredictions } from '@lib/autocomplete';
import { google_api_key } from "@config";

import Layout from "@pages/Layout";

const multiselect_strings = {
  allItemsAreSelected: t('multiselect.allItemsAreSelected'),
  clearSearch: t('multiselect.clearSearch'),
  noOptions: t('multiselect.noOptions'),
  search: t('multiselect.search'),
  selectAll: t('multiselect.selectAll'),
  selectSomeItems: t('multiselect.selectSomeItems'),
};

const EditProfileCandidateScreen = () => {
    const initForm = {
        longitude: "",
        latitude: "",
        city: "",
        department: "",
        region: "",
        search_radius: undefined,
    };
    const initDataList = {
        fonctions: [],
        activities: [],
        relationship_status: [],
        experience: [],
        situation: [],
        contracts: []
    };
  
    const [form, setForm] = useState(initForm);
    const [selectData, setSelectData] = useState(initDataList);
    const [isLoaded, setIsLoaded] = useState(false);

    const history = useHistory();
    const toast = useToast();
    const { register, handleSubmit, setValue, getValues, control, formState: { errors } } = useForm({
        resolver: yupResolver(editProfileCandidateSchema)
    });

    const isArray = (arr) => {
        if (Array.isArray(arr)) return true;
        return false;
    };
    const preventDefaultSubmit = (e) => {
        if (e.code === 'Enter') e.preventDefault();
    };

    const onLoad = async () => {
        const { email, address, longitude, latitude, city, region, department, person, phone } = loadState('user-data');
      
        setForm({
            address,
            longitude,
            latitude,
            city,
            department,
            region,
            search_radius: person.search_radius && person.search_radius
        });

        setValue('email', email);
        setValue('address', address);
        setValue('first_name', person.first_name);
        setValue('last_name', person.last_name);
        setValue('phone', phone);
        setValue('activities', isArray(person.activities) ? 
                                person.activities.map(el => ({label: el.name, value: el.id})) : 
                                [{ label: person.activities.name, value: person.activities.id }]);
        setValue('fonctions', isArray(person.fonctions) ? 
                                person.fonctions.map(el => ({label: el.name, value: el.id})) : 
                                [{ label: person.fonctions.name, value: person.fonctions.id }]);
        setValue('situation', person.situation ? person.situation.id : "");
        setValue('experience', person.experience ? person.experience.id : "");
        setValue('birthday', person.birthday ? person.birthday.split('/').reverse().join('-') : "");
        setValue('relationship_status', person.relationship_status ? person.relationship_status.id: "");
        setValue('driving_license', person.driving_license === 1 ? true : person.driving_license === 0 ? false : false);
        setValue('has_vehicle', person.has_vehicle === 1 ? true : person.has_vehicle === 0 ? false : false);
        setValue('has_handicap', person.has_handicap === 1 ? true : person.has_handicap === 0 ? false : false);
        setValue('search_radius', person.search_radius && person.search_radius);
        setValue('preferred_contracts', isArray(person.preferred_contracts) ? 
                                        person.preferred_contracts.map(el => ({label: el.name, value: el.id})) : 
                                        [{ label: person.preferred_contracts.name, value: person.preferred_contracts.id }]);       
    };
    const onEdit = async (data) => {
        const { email, address, phone, first_name, last_name, birthday, situation, fonctions, activities, experience, relationship_status, preferred_contracts, driving_license, has_vehicle, has_handicap } = data;
        const { region, department, city, longitude, latitude, search_radius } = form;

        let new_data = {
            email, address, search_radius, phone, first_name, last_name, birthday, situation, relationship_status, experience,
            driving_license, has_vehicle, has_handicap,
            fonctions: fonctions.map(el => el.value).join(','), 
            preferred_contracts: preferred_contracts.map(el => el.value).join(','), 
            activities: activities.map(el => el.value).join(','),
            region, department, city, longitude, latitude
        };

        setIsLoaded(true);
        const result = await edit_profile(new_data);

        if (result.success) {
            setIsLoaded(false);
            removeState("user-data");
            saveState("user-data", result.data.user)
            toast({ 
                description: t('messages.success.edit'),
                status: "success", position: "bottom-right", variant: "left-accent", isClosable: true 
            })
            history.push('/candidate/profile')
            return;
        }
        setIsLoaded(false);
        toast({ 
            description: t('messages.error.edit'),
            status: "error", position: "bottom-right", variant: "left-accent", isClosable: true 
        })
    };
     // load data list
    const getDataList = async () => {
        const data = await dataList();

        setSelectData({
            fonctions: data.fonctions.map(el => ({ value: el.id, label: el.name })),
            activities: data.activities.map(el => ({ value: el.id, label: el.name })),
            experience: data.experience,
            relationship_status: data.statuses,
            situation: data.situations,
            contracts: data.contracts.map(el => ({ value: el.id, label: el.name }))
        });
    };

    /* Localization */
    const onPlaceSelected = (params) => {
        setForm(prevState => {
            return { ...prevState, ...params }
        });
    };

    useEffect(() => {
        getDataList();
    }, []);

    useEffect(() => {
        onLoad();
    }, []);

    return(
        <Layout menuType="candidate" isNavBarVisible isFooterVisible> 
            <Container maxW="1024px" bg="white" minHeight='calc(100vh - 100px)' py="36px" px={["16px","","","100px"]} borderRadius="4px">
                <Flex flexDir="column">
                    <Heading textAlign="center" fontSize="30px" m="auto" maxW="435px"> {t('candidate.profile.edit.title')} </Heading>
                    <Separator />

                    <form onSubmit={handleSubmit(onEdit)} onKeyDown={(e) => preventDefaultSubmit(e)}>
                        <Flex flexDir="row" flexWrap="wrap" justifyContent={{base: "center", md: "space-around", lg: "space-between"}}>
                            <FormControl id="first_name" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                <FormLabel> { t('fields.candidates.first_name') } </FormLabel>
                                <Input
                                    type="text"
                                    placeholder={ t('fields.candidates.first_name') }
                                    borderColor="gray.borders"
                                    borderRadius="4px"
                                    bg="white"
                                    maxWidth="385px"
                                    h="40px"
                                    focusBorderColor={errors.first_name ? "error" : "blue.light"}
                                    isInvalid={errors.first_name ? true : false}
                                    {...register("first_name")}
                                />
                                {errors.first_name && <ErrorBox error={errors.first_name.message} />}
                            </FormControl>
                            
                            <FormControl id="last_name" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                <FormLabel> { t('fields.candidates.last_name') } </FormLabel>
                                <Input
                                    type="text"
                                    placeholder={ t('fields.candidates.last_name') }
                                    borderColor="gray.borders"
                                    borderRadius="4px"
                                    bg="white"
                                    maxWidth="385px"
                                    h="40px"
                                    focusBorderColor={errors.last_name ? "error" : "blue.light"}
                                    isInvalid={errors.last_name ? true : false}
                                    {...register("last_name")}
                                />
                                {errors.last_name && <ErrorBox error={errors.last_name.message} />}
                            </FormControl>

                            <FormControl id="phone" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                <FormLabel> { t('fields.candidates.phone') } </FormLabel>
                                <Input
                                    type="text"
                                    placeholder={ t('fields.candidates.phone') }
                                    borderColor="gray.borders"
                                    borderRadius="4px"
                                    bg="white"
                                    maxWidth="385px"
                                    h="40px"
                                    focusBorderColor={errors.phone ? "error" : "blue.light"}
                                    isInvalid={errors.phone ? true : false}
                                    {...register("phone")}
                                />
                                {errors.phone && <ErrorBox error={errors.phone.message} />}
                            </FormControl>

                            <FormControl id="email" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                <FormLabel> { t('fields.email') } </FormLabel>
                                <Input
                                    type="text"
                                    placeholder={ t('fields.email') }
                                    borderColor="gray.borders"
                                    borderRadius="4px"
                                    bg="white"
                                    maxWidth="385px"
                                    h="40px"
                                    focusBorderColor={errors.email ? "error" : "blue.light"}
                                    isInvalid={errors.email ? true : false}
                                    {...register("email")}
                                />
                                {errors.email && <ErrorBox error={errors.email.message} />}
                            </FormControl>
                        </Flex>

                        <Heading as="h3" fontSize="20px" textColor="black" textAlign="center" mt="1rem" mb="1.5rem" >
                            {t('company.profile.edit.optional_info_1')}
                            <Box as="span" fontWeight="400" fontSize="14px" pl=".2rem">
                                {t('company.profile.edit.optional_info_2')}
                            </Box>
                        </Heading>

                        <Flex flexDir="row" flexWrap="wrap" justifyContent={{base: "center", md: "space-around", lg: "space-between"}}>
                            <FormControl id="birthday" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                <FormLabel> { t('fields.candidates.birthday') } </FormLabel>
                                    <Input
                                        type="date"
                                        borderColor="gray.borders"
                                        borderRadius="4px"
                                        bg="white"
                                        maxWidth="385px"
                                        h="40px"
                                        focusBorderColor={errors.birthday ? "error" : "blue.light"}
                                        isInvalid={errors.birthday ? true : false}
                                        {...register("birthday")}
                                        min="1900-01-01" max={new Date().toISOString().slice(0, 10)}
                                    />                    
                            </FormControl>

                            <Script url={`https://maps.googleapis.com/maps/api/js?key=${google_api_key}&libraries=places`}
                                onLoad={() => getPredictions({ inputId:'autocomplete', callback: onPlaceSelected })}
                            />
                            <FormControl id="address" mb="1.625rem" pos="relative" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                <FormLabel> { t('fields.company.address') } </FormLabel>
                                <InputGroup>
                                    <Input type="text"
                                        placeholder="Ville, département, région..."
                                        _placeholder={{color:'#AAA'}}
                                        borderColor="gray.borders"
                                        borderRadius="3px"
                                        bg="white"
                                        maxWidth="385px"
                                        h="40px"
                                        focusBorderColor={errors.address ? "error" : "blue.light"}
                                        autoComplete="off"
                                        isInvalid={errors.address ? true : false}
                                        {...register("address")}
                                        id="autocomplete"
                                    />

                                    <InputRightElement
                                    children={<IconLocation iconType="candidate" />}
                                    top="-2px"
                                    />
                                </InputGroup>

                                {errors.address && <ErrorBox error={errors.address.message} />}
                            </FormControl>

                            <View if={form.search_radius || form.search_radius === 0} m="36px auto 30px auto">
                                <AddressSlider setForm={setForm} form={form} />
                            </View>

                            <Flex flexDir="row" flexWrap="wrap" justifyContent={{base: "center", md: "space-around", lg: "space-between"}}>
                                <FormControl id="situation" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                    <FormLabel> { t('fields.candidates.situation_pro') } </FormLabel> 
                                        <Select
                                            placeholder={ t('fields.candidates.situation_pro') }                         
                                            errorBorderColor="error"
                                            borderColor="gray.borders"
                                            borderRadius="4px"
                                            bg="white"
                                            maxW="385px"
                                            h="40px"
                                            focusBorderColor={errors.situation ? "error" : "blue.light"}
                                            isInvalid={errors.situation ? true : false}
                                            icon={<IconTriangle iconType="candidate" position="down" />}
                                            {...register("situation")}
                                        >
                                            <option value="actively_searching"> Recherche active </option>
                                            <option value="passively_searching"> Recherche passive (déjà en poste) </option>
                                        </Select>
                                    {errors.situation && <ErrorBox error={errors.situation.message} />}
                                </FormControl>

                                <FormControl id="fonctions" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                    <FormLabel> { t('fields.company.metier') } </FormLabel>
                                        <Controller name="fonctions" render={({ field }) => (
                                            <MultiSelect
                                                {...field}
                                                options={selectData.fonctions}
                                                onChange={(val) => setValue('fonctions', val)}
                                                ArrowRenderer={() => (<IconTriangle iconType="candidate" position="down" />)}
                                                valueRenderer={(val) => !val.length ? t("fields.company.fonctions") : null}
                                                className={("className", errors.fonctions ? "multi-select candidate register error" : "multi-select candidate register")}
                                                overrideStrings={multiselect_strings}
                                            />
                                            )}
                                            control={control}
                                            defaultValue=""
                                        />
                                    {errors.fonctions && <ErrorBox error={errors.fonctions.message} />}
                                </FormControl>


                                <FormControl id="activities" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                    <FormLabel> { t('fields.company.activities') } </FormLabel>
                                        <Controller name="activities" render={({ field }) => (
                                            <MultiSelect
                                                {...field}
                                                options={selectData.activities}
                                                onChange={(val) => setValue('activities', val)}
                                                ArrowRenderer={() => (<IconTriangle iconType="candidate" position="down" />)}
                                                valueRenderer={(val) => !val.length ? t("fields.company.activities") : null}
                                                className={("className", errors.activities ? "multi-select candidate register error" : "multi-select candidate register")}
                                                overrideStrings={multiselect_strings}
                                            />
                                            )}
                                            control={control}
                                            defaultValue=""
                                        />
                                    {errors.activities && <ErrorBox error={errors.activities.message} />}
                                </FormControl>

                                <FormControl id="experience" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                    <FormLabel> { t('fields.company.experience') } </FormLabel>
                                        <Select
                                            placeholder={ t('fields.company.experience') }                         
                                            errorBorderColor="error"
                                            borderColor="gray.borders"
                                            borderRadius="4px"
                                            bg="white"
                                            maxW="385px"
                                            h="40px"
                                            focusBorderColor={errors.experience ? "error" : "blue.light"}
                                            isInvalid={errors.experience ? true : false}
                                            icon={<IconTriangle iconType="candidate" position="down" />}
                                            {...register("experience")}
                                        >
                                            <option value="0"> Débutant </option>
                                            <option value="1"> 1 an </option>
                                            <option value="2"> 2 ans </option>
                                            <option value="3"> 3 ans </option>
                                            <option value="4"> 4 ans </option>
                                            <option value="5"> 5 ans </option>
                                            <option value="6"> 6 ans </option>
                                            <option value="7"> 7 ans </option>
                                            <option value="8"> 8 ans </option>
                                            <option value="8"> 9 ans </option>
                                            <option value="10"> 10 ans </option>
                                            <option value="11"> Plus de 10 ans </option>
                                        </Select>                                      
                                    {errors.experience && <ErrorBox error={errors.experience.message} />}
                                </FormControl>

                                <FormControl id="relationship_status" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                    <FormLabel> { t('fields.company.civil_status') } </FormLabel>
                                        <Select
                                            placeholder={ t('fields.company.civil_status') }                         
                                            errorBorderColor="error"
                                            borderColor="gray.borders"
                                            borderRadius="4px"
                                            bg="white"
                                            maxW="385px"
                                            h="40px"
                                            focusBorderColor={errors.relationship_status ? "error" : "blue.light"}
                                            isInvalid={errors.relationship_status ? true : false}
                                            icon={<IconTriangle iconType="candidate" position="down" />}
                                            {...register("relationship_status")}
                                        >
                                            <option value="single"> Célibataire </option>
                                            <option value="married"> Marié </option>
                                        </Select>                                 
                                    {errors.relationship_status && <ErrorBox error={errors.relationship_status.message} />}
                                </FormControl>  
                                
                                <FormControl id="preferred_contracts" mb="1.625rem" maxW={["85vw", "", "21.5rem", ""]} w={["100%", "90%", "", ""]}>
                                    <FormLabel> { t('fields.candidates.preferred_contracts') } </FormLabel>
                                        <Controller name="preferred_contracts" render={({ field }) => (
                                            <MultiSelect
                                                {...field}
                                                options={selectData.contracts}
                                                onChange={(val) => setValue('preferred_contracts', val)}
                                                ArrowRenderer={() => (<IconTriangle iconType="candidate" position="down" />)}
                                                valueRenderer={(val) => !val.length ? t("fields.candidates.preferred_contracts") : null}
                                                className={("className", errors.preferred_contracts ? "multi-select candidate register error" : "multi-select candidate register")}
                                                overrideStrings={multiselect_strings}
                                            />
                                            )}
                                            control={control}
                                            defaultValue=""
                                        />
                                    {errors.preferred_contracts && <ErrorBox error={errors.preferred_contracts.message} />}
                                </FormControl>

                                <FormControl id="checkboxes" mb="1.625rem">
                                    <Flex flexDir="row" flexWrap="wrap" justifyContent={["center", "", "", ""]} maxW={["385px", "initial", "", ""]} m="0 auto">
                                        <label className={"custom-checkbox custom-checkbox-register"} tab-index="0" aria-label={"driving_license"}>
                                            <input type="checkbox" id={"driving_license"} value={true} {...register("driving_license")} />
                                            <span className={"checkmark candidate"}></span>
                                            <label className="label" htmlFor={"driving_license"}>
                                                { t('fields.candidates.permis') } 
                                            </label>
                                        </label>
                                        {errors.driving_license && <ErrorBox error={errors.driving_license.message} />}

                                        <label className={"custom-checkbox custom-checkbox-register"} tab-index="0" aria-label={"has_vehicle"}>
                                            <input type="checkbox" id={"has_vehicle"} value={true} {...register("has_vehicle")} />
                                            <span className={"checkmark candidate"}></span>
                                            <label className="label" htmlFor={"has_vehicle"}>
                                                { t('fields.candidates.has_vehicle') } 
                                            </label>
                                        </label>
                                        {errors.has_vehicle && <ErrorBox error={errors.has_vehicle.message} />}

                                        <label className={"custom-checkbox custom-checkbox-register"} tab-index="0" aria-label={"handicap"}>
                                            <input type="checkbox" id={"handicap"} value={true} {...register("handicap")} />
                                            <span className={"checkmark candidate"}></span>
                                            <label className="label" htmlFor={"handicap"}>
                                                { t('fields.candidates.handicap') } 
                                            </label>
                                        </label>
                                        {errors.handicap && <ErrorBox error={errors.handicap.message} />}
                                        
                                    </Flex>
                                </FormControl> 
                            </Flex>                    
                        </Flex>

                        <Button type="submit" w={["100%", "90%", "", ""]} maxW={["65vw", "344px", "", ""]} h="40px" borderRadius="24px" border="2px solid" borderColor="blue.main" m="26px auto 0 auto" bg="blue.main" color="white" display="flex"
                            _hover={{ bg: "blue.light", borderColor: "blue.light" }}>
                            {isLoaded? <Spinner size="md" /> : t('global.confirm_1')}
                        </Button>
                    </form>
               </Flex> 
            </Container>
        </Layout>
    )
}

export default EditProfileCandidateScreen;